import type { Salarie } from '@shared/types/Salarie';
import type { Tenant } from '@shared/types/Tenant';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Conge, TypeConge } from '@shared/types/Conge';
import type { AttestationType } from '@shared/types/Attestation';
import { IndemnitesFinContratService } from '@domain/rh/IndemnitesFinContrat';
import { ancienneteAnneesFractionnaires } from '@domain/rh/PreavisLegal';
import { DEFAULT_STC_CONFIG, type StcConfig } from '@application/rh/StcConfig';
import { calculerIndemniteCP, calculerSoldeCongesJours } from '@domain/rh/SoldeToutCompte';
import { fmtNumber, fmtFCFA, fmtDate, fmtPeriode, esc } from '@presentation/shell/format';
import { formatPayrollRate, PAYROLL_PARAMETERS } from '@shared/constants/PayrollParameters';
import {
  brandCenterStripHtml,
  brandFontStyleBlock,
  brandHeaderTextHtml,
  brandRootClass,
  brandRootStyleAttr,
  documentThemeFor,
  nxBannerLeftHtml,
  nxBannerRightLogoHtml,
} from '@presentation/print/documentBrand';
import { companyLegalFromTenant } from '@domain/legal/CompanyLegalInfo';
import { ventilationCnssDuBulletin } from '@domain/paie/VentilationCnss';

export type { AttestationType };

export interface AttestationResult {
  title: string;
  filename: string;
  html: string;
}

const TITRES: Record<AttestationType, { title: string; subtitle: string; cls: string }> = {
  travail: { title: 'ATTESTATION DE TRAVAIL', subtitle: '', cls: 'nx-travail' },
  salaire: {
    title: 'ATTESTATION DE SALAIRE',
    subtitle: 'Justificatif de rémunération',
    cls: 'nx-salaire',
  },
  presence: { title: 'CERTIFICAT DE PRÉSENCE', subtitle: '', cls: 'nx-presence' },
  fin_contrat: {
    title: 'SOLDE DE TOUT COMPTE',
    subtitle: 'Document de fin de contrat',
    cls: 'nx-solde',
  },
  conges: {
    title: 'ATTESTATION DE CONGÉ',
    subtitle: 'Justificatif de congés payés',
    cls: 'nx-conges',
  },
  cnss: {
    title: 'BORDEREAU CNSS',
    subtitle: 'Récapitulatif des cotisations sociales',
    cls: 'nx-cnss',
  },
};

function anciennete(dateEmbauche: string): string {
  const d = new Date(dateEmbauche);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  let mois = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (mois < 0) mois = 0;
  const ans = Math.floor(mois / 12);
  const rm = mois % 12;
  if (ans === 0) return `${rm} mois`;
  return rm === 0 ? `${ans} an${ans > 1 ? 's' : ''}` : `${ans} an${ans > 1 ? 's' : ''} ${rm} mois`;
}

function infoBox(sal: Salarie): string {
  const contrat = sal.statut_contrat ?? 'CDI';
  return `<div class="nx-info-box">
    <div class="nx-row"><span class="nx-lbl">Nom &amp; prénom</span><span class="nx-val">${esc(sal.nom)} ${esc(sal.prenom)}</span></div>
    <div class="nx-row"><span class="nx-lbl">Matricule</span><span class="nx-val" style="font-family:'Courier New',monospace;">${esc(sal.matricule)}</span></div>
    <div class="nx-row"><span class="nx-lbl">Date de naissance</span><span class="nx-val">${esc(fmtDate(sal.date_naissance))}</span></div>
    <div class="nx-row"><span class="nx-lbl">Fonction</span><span class="nx-val">${esc(sal.poste || '—')}</span></div>
    <div class="nx-row"><span class="nx-lbl">Type de contrat</span><span class="nx-val">${esc(contrat)}</span></div>
    <div class="nx-row"><span class="nx-lbl">Date d'embauche</span><span class="nx-val">${esc(fmtDate(sal.date_embauche))}</span></div>
    <div class="nx-row"><span class="nx-lbl">Ancienneté</span><span class="nx-val">${esc(anciennete(sal.date_embauche))}</span></div>
  </div>`;
}

function contenuTravail(sal: Salarie, t: Tenant | null, ville: string): string {
  const contrat = sal.statut_contrat ?? 'CDI';
  const dureeContrat =
    contrat === 'CDI'
      ? 'durée indéterminée (CDI)'
      : contrat === 'CDD'
        ? `durée déterminée (CDD)${sal.date_fin_contrat ? ", allant jusqu'au " + fmtDate(sal.date_fin_contrat) : ''}`
        : contrat;
  return `
    <p>Je soussigné(e), agissant en qualité de <strong>Directeur des Ressources Humaines</strong> de la société <strong>${esc(t?.nom || 'la société')}</strong>, ayant son siège social à ${esc(t?.adresse || ville)}, immatriculée au Registre du Commerce et du Crédit Mobilier sous le n° <strong>${esc(t?.rccm || '—')}</strong> et au NIF n° <strong>${esc(t?.nif || '—')}</strong>,</p>
    <p style="font-weight:700; margin-top:18px; color:var(--nx-marine); font-size:13pt; letter-spacing:0.5px;">CERTIFIE QUE:</p>
    <p>Monsieur / Madame <strong>${esc(sal.nom)} ${esc(sal.prenom)}</strong>, né(e) le <strong>${esc(fmtDate(sal.date_naissance))}</strong>${sal.lieu_naissance ? ' à <strong>' + esc(sal.lieu_naissance) + '</strong>' : ''}, de nationalité <strong>${esc(sal.nationalite || 'gabonaise')}</strong>${sal.cni ? ", titulaire de la pièce d'identité n° <strong>" + esc(sal.cni) + '</strong>' : ''}, est employé(e) au sein de notre société depuis le <strong>${esc(fmtDate(sal.date_embauche))}</strong>, soit une ancienneté de <strong>${esc(anciennete(sal.date_embauche))}</strong>, en qualité de <strong>${esc(sal.poste || 'salarié(e)')}</strong>, en vertu d'un contrat de travail à <strong>${esc(dureeContrat)}</strong>, régi par le Code du Travail de la République Gabonaise (loi n° 022/2021)${t?.secteur_activite ? ' et la convention collective applicable au secteur <strong>' + esc(t.secteur_activite) + '</strong>' : ''}.</p>
    ${infoBox(sal)}
    <p style="margin-top:14px;">L'intéressé(e) exerce ses fonctions à temps plein, dans le respect des procédures internes et à la pleine satisfaction de sa hiérarchie.</p>
    <p style="font-weight:700; margin-top:18px; color:var(--nx-marine);">Mentions complémentaires:</p>
    <ul style="margin: 6px 0 14px 0; padding-left: 20px; line-height: 1.7;">
      <li>L'intéressé(e) est régulièrement déclaré(e) auprès de la <strong>CNSS</strong> et de la <strong>CNAMGS</strong>, conformément à la législation sociale gabonaise en vigueur.</li>
      <li>À la date de signature, le contrat de travail est en cours d'exécution; il n'a fait l'objet <strong>d'aucune procédure de rupture, de licenciement, de démission ni de mise à pied</strong>.</li>
      <li>Conformément à l'<strong>article 86 du Code du Travail gabonais</strong>, la présente attestation ne contient aucune mention défavorable au salarié.</li>
    </ul>
    <div class="nx-legal-mention"><strong>Validité:</strong> La présente attestation est délivrée à la demande de l'intéressé(e) pour servir et valoir ce que de droit. <strong>Durée de validité: trois (3) mois</strong> à compter de la date de signature.</div>`;
}

function contenuSalaire(sal: Salarie, t: Tenant | null, b: Bulletin | undefined): string {
  const brut = b ? b.brut_imposable : sal.salaire_base;
  const net = b ? b.net_a_payer : Math.round(sal.salaire_base * 0.78);
  const charges = b ? b.total_charges_emp : Math.round(sal.salaire_base * 0.21);
  const cout = b ? b.cout_employeur : brut + charges;
  const irpp = b ? b.irpp_mensuel : 0;
  const cnssRet = b ? b.cnss_vid_sal : 0;
  const cnamgsRet = b ? b.cnamgs_sal : 0;
  const periode = b ? fmtPeriode(b.periode) : 'période courante';
  return `
    <p>Je soussigné(e), agissant en qualité de <strong>Directeur des Ressources Humaines</strong> de la société <strong>${esc(t?.nom || 'la société')}</strong>, immatriculée au RCCM n° <strong>${esc(t?.rccm || '—')}</strong> et au NIF n° <strong>${esc(t?.nif || '—')}</strong>, certifie sur l'honneur que la personne ci-après désignée est en activité au sein de la société et perçoit les rémunérations détaillées dans le présent document.</p>
    ${infoBox(sal)}
    <div class="nx-salary-hero">
      <div class="nx-sh-label">Net mensuel à payer</div>
      <div class="nx-sh-value">${fmtFCFA(net)}</div>
      <div class="nx-sh-sub">Période de référence: ${esc(periode)}</div>
    </div>
    <div class="nx-highlight-cards">
      <div class="nx-highlight-card"><div class="nx-hc-label">Salaire brut</div><div class="nx-hc-value">${fmtNumber(brut)}</div><div class="nx-hc-sub">FCFA / mois</div></div>
      <div class="nx-highlight-card cyan"><div class="nx-hc-label">Charges patronales</div><div class="nx-hc-value">${fmtNumber(charges)}</div><div class="nx-hc-sub">FCFA</div></div>
      <div class="nx-highlight-card menthe"><div class="nx-hc-label">Coût employeur</div><div class="nx-hc-value">${fmtNumber(cout)}</div><div class="nx-hc-sub">FCFA / mois</div></div>
    </div>
    <p style="margin-top:20px; font-weight:700; color:var(--nx-marine);">Détail de la rémunération mensuelle brute:</p>
    <table class="nx-amounts">
      <thead><tr><th>Désignation</th><th class="num">Base</th><th class="num">Montant (FCFA)</th></tr></thead>
      <tbody>
        <tr><td>Salaire brut imposable</td><td class="num">—</td><td class="num">${fmtNumber(brut)}</td></tr>
        <tr><td>Retenue CNSS — Pension VID (${formatPayrollRate(PAYROLL_PARAMETERS.cnssPensionEmployee)})</td><td class="num">${fmtNumber(Math.min(brut, PAYROLL_PARAMETERS.cnssCeiling))}</td><td class="num">- ${fmtNumber(cnssRet)}</td></tr>
        <tr><td>Retenue CNAMGS (${formatPayrollRate(PAYROLL_PARAMETERS.cnamgsEmployee)})</td><td class="num">${fmtNumber(Math.min(brut, PAYROLL_PARAMETERS.cnamgsCeiling))}</td><td class="num">- ${fmtNumber(cnamgsRet)}</td></tr>
        <tr><td>IRPP — barème progressif</td><td class="num">—</td><td class="num">- ${fmtNumber(irpp)}</td></tr>
        <tr class="nx-total"><td colspan="2">NET MENSUEL À PAYER</td><td class="num">${fmtNumber(net)}</td></tr>
      </tbody>
    </table>
    <p style="margin-top:18px;">L'intéressé(e) est en activité dans notre société depuis le <strong>${esc(fmtDate(sal.date_embauche))}</strong> (${esc(anciennete(sal.date_embauche))}).</p>
    <div class="nx-legal-mention"><strong>Validité:</strong> Délivrée pour servir et valoir ce que de droit, notamment pour les démarches bancaires, administratives ou de logement. <strong>Durée de validité: trois (3) mois</strong>.</div>`;
}

function contenuPresence(sal: Salarie, t: Tenant | null, today: string): string {
  return `
    <p>Je soussigné(e), <strong>Directeur des Ressources Humaines</strong> de la société <strong>${esc(t?.nom || 'la société')}</strong>, certifie par la présente la <strong>présence effective et continue</strong> du collaborateur ci-après désigné dans nos effectifs:</p>
    ${infoBox(sal)}
    <div class="nx-presence-timeline">
      <div class="nx-pt-point active"><div class="nx-pt-dot"></div><div class="nx-pt-label">Embauche</div><div class="nx-pt-date">${esc(fmtDate(sal.date_embauche))}</div></div>
      <div class="nx-pt-line"></div>
      <div class="nx-pt-point active"><div class="nx-pt-dot"></div><div class="nx-pt-label">Période</div><div class="nx-pt-date">${esc(anciennete(sal.date_embauche))}</div></div>
      <div class="nx-pt-line"></div>
      <div class="nx-pt-point active"><div class="nx-pt-dot"></div><div class="nx-pt-label">Aujourd'hui</div><div class="nx-pt-date">${esc(today)}</div></div>
    </div>
    <p>L'intéressé(e) y travaille de façon régulière, à <strong>temps plein</strong>, depuis le <strong>${esc(fmtDate(sal.date_embauche))}</strong>, sans interruption d'activité, et fait toujours partie des effectifs au jour de l'établissement du présent certificat.</p>
    <div class="nx-legal-mention"><strong>Authentification:</strong> Le présent certificat est délivré à la demande de l'intéressé(e) pour servir et valoir ce que de droit. Toute falsification est passible des sanctions prévues par le Code pénal gabonais.</div>`;
}

function contenuFinContrat(
  sal: Salarie,
  t: Tenant | null,
  today: string,
  conges: readonly Conge[] = [],
  stc: StcConfig = DEFAULT_STC_CONFIG,
): string {
  const base = sal.salaire_base;
  const annees = IndemnitesFinContratService.calculerAnciennete(sal.date_embauche, sal.date_sortie);
  const cfg = stc ?? DEFAULT_STC_CONFIG;
  const congesJours = calculerSoldeCongesJours(sal, cfg, conges);
  const indemniteConges = calculerIndemniteCP(sal, cfg, conges);
  // Art. 90 : les fractions d'année sont prises en compte (mois complets).
  const indemniteLic = IndemnitesFinContratService.calculerIndemniteLicenciement(
    base,
    ancienneteAnneesFractionnaires(sal.date_embauche, sal.date_sortie),
  );
  const total = base + indemniteConges + indemniteLic;
  const finContrat = sal.date_sortie ?? sal.date_fin_contrat;
  const dateSortie = finContrat ? fmtDate(finContrat) : today;
  return `
    <p>Je soussigné(e), <strong>${esc(sal.nom)} ${esc(sal.prenom)}</strong>, matricule <strong>${esc(sal.matricule)}</strong>, reconnais avoir reçu ce jour de la société <strong>${esc(t?.nom || 'la société')}</strong> la somme globale détaillée ci-après en règlement de tous droits, salaires, indemnités et accessoires liés au contrat de travail ayant lié les parties.</p>
    ${infoBox(sal)}
    <p style="margin-top:18px; font-weight:700; color:var(--nx-marine);">Détail du solde de tout compte:</p>
    <table class="nx-amounts">
      <thead><tr><th>Désignation</th><th class="num">Base de calcul</th><th class="num">Montant (FCFA)</th></tr></thead>
      <tbody>
        <tr><td><strong>Salaire du dernier mois</strong></td><td class="num">Dernier mois</td><td class="num">${fmtNumber(base)}</td></tr>
        <tr><td><strong>Indemnité compensatrice de congés payés</strong><br><small style="color:var(--nx-gris,#888);">${congesJours} jour(s) acquis</small></td><td class="num">${congesJours} j</td><td class="num">${fmtNumber(indemniteConges)}</td></tr>
        ${indemniteLic > 0 ? `<tr><td><strong>Indemnité de licenciement</strong><br><small style="color:#888;">${esc(anciennete(sal.date_embauche))} · Code du travail, art. 87 et 90</small></td><td class="num">${Math.floor(annees)} an(s)</td><td class="num">${fmtNumber(indemniteLic)}</td></tr>` : ''}
        <tr class="nx-total"><td colspan="2"><strong style="font-size:13pt;">TOTAL SOLDE DE TOUT COMPTE</strong></td><td class="num"><strong style="font-size:14pt;">${fmtNumber(total)}</strong></td></tr>
      </tbody>
    </table>
    <p style="margin-top:20px;">Les sommes ci-dessus m'ont été versées pour les services accomplis jusqu'au <strong>${esc(dateSortie)}</strong>, date à laquelle ledit contrat a pris fin.</p>
    <div class="nx-legal-mention" style="background:#FBE9E7; border-left-color:#BF360C;"><strong>Portée du reçu:</strong> Conformément à l'article 183 du Code du travail gabonais, la mention « pour solde de tout compte » ou toute mention équivalente souscrite par le travailleur n'est pas opposable à celui-ci : elle ne vaut pas renonciation à tout ou partie des droits qu'il tient de son contrat de travail. L'acceptation sans réserve d'un bulletin de paie ne vaut pas non plus renonciation.</div>
    <div class="nx-double-sign">
      <div class="nx-sign-col"><div class="nx-sign-title">Le salarié</div><div class="nx-sign-name">${esc(sal.nom)} ${esc(sal.prenom)}</div><div class="nx-sign-role">Matricule ${esc(sal.matricule)}</div><div class="nx-sign-mention">"Lu et approuvé,<br>Bon pour solde de tout compte"</div></div>
      <div class="nx-sign-col"><div class="nx-sign-title">L'employeur</div><div class="nx-sign-name">Pour ${esc(t?.nom || '')}</div><div class="nx-sign-role">Directeur des Ressources Humaines</div></div>
    </div>`;
}

const TYPE_CONGE_LABEL: Record<TypeConge, string> = {
  annuel: 'Congé payé annuel',
  maternite: 'Congé de maternité',
  paternite: 'Congé de paternité',
  maladie: 'Arrêt maladie',
  evenement_familial: 'Événement familial',
  sans_solde: 'Congé sans solde',
  autre: 'Absence autorisée',
};

function contenuConges(sal: Salarie, t: Tenant | null, conge?: Conge): string {
  if (!conge) {
    return `<p>Aucun congé approuvé n’est enregistré pour ce salarié. Validez d’abord une demande de congé.</p>${infoBox(sal)}`;
  }
  const typeLabel = TYPE_CONGE_LABEL[conge.type];
  return `
    <p>Je soussigné(e), <strong>Directeur des Ressources Humaines</strong> de la société <strong>${esc(t?.nom || 'la société')}</strong>, certifie que le collaborateur ci-après désigné bénéficie d'une période de <strong>${esc(typeLabel.toLowerCase())}</strong> conformément au planning établi et aux dispositions de la convention collective applicable.</p>
    ${infoBox(sal)}
    <div class="nx-period-block">
      <div class="nx-period-side start"><div class="nx-ps-label">Début du congé</div><div class="nx-ps-date">${esc(fmtDate(conge.date_debut))}</div></div>
      <div class="nx-period-arrow">→</div>
      <div class="nx-period-side end"><div class="nx-ps-label" style="color:#888;">Reprise prévue</div><div class="nx-ps-date">${esc(fmtDate(conge.date_fin))}</div></div>
    </div>
    <div class="nx-highlight-cards" style="grid-template-columns: repeat(2, 1fr);">
      <div class="nx-highlight-card"><div class="nx-hc-label">Durée totale</div><div class="nx-hc-value">${conge.jours}</div><div class="nx-hc-sub">${conge.type === 'annuel' ? 'jours ouvrables' : 'jours calendaires'}</div></div>
      <div class="nx-highlight-card cyan"><div class="nx-hc-label">Type d'absence</div><div class="nx-hc-value" style="font-size:13pt;">${esc(typeLabel)}</div></div>
    </div>
    <p>Pendant cette période d'absence autorisée, l'intéressé(e) demeure protégé(e) par les dispositions du <strong>Code du travail gabonais</strong> et conserve l'intégralité de ses droits sociaux (CNSS, CNAMGS) ainsi que sa rémunération habituelle.</p>
    <div class="nx-legal-mention"><strong>Mention:</strong> La présente attestation tient lieu d'autorisation officielle d'absence. Toute prolongation doit faire l'objet d'une nouvelle autorisation écrite de la Direction des Ressources Humaines.</div>`;
}

function contenuCnss(sal: Salarie, b: Bulletin | undefined): string {
  if (!b) {
    return `<p>Aucun bulletin de paie n'est disponible pour ce salarié. Calculez d'abord la paie afin d'éditer le bordereau CNSS.</p>${infoBox(sal)}`;
  }
  const totalCnss = b.cnss_pf_emp + b.cnss_rp_emp + b.cnss_vid_emp + b.cnss_vid_sal;
  const totalEmp = b.cnss_pf_emp + b.cnss_rp_emp + b.cnss_vid_emp;
  // Ventilation 5 % / 2 % / 11 % (un bulletin ancien, tout sur `cnss_pf_emp`, est reconstitué pour l'affichage sans changer son total).
  const ventilation = ventilationCnssDuBulletin(b);
  const totalEmployerRate =
    PAYROLL_PARAMETERS.cnssPfEmployer +
    PAYROLL_PARAMETERS.cnssRiskEmployer +
    PAYROLL_PARAMETERS.cnssPensionEmployer;
  const totalRate = totalEmployerRate + PAYROLL_PARAMETERS.cnssPensionEmployee;
  return `
    <div class="nx-cnss-header">
      <div class="nx-cnss-logo">CNSS</div>
      <div class="nx-cnss-info"><div class="nx-cnss-title">Caisse Nationale de Sécurité Sociale du Gabon</div><div class="nx-cnss-sub">Bordereau nominatif de cotisations · Période ${esc(fmtPeriode(b.periode))}</div></div>
    </div>
    <p>Récapitulatif des cotisations sociales versées à la <strong>CNSS</strong> pour la période de <strong>${esc(fmtPeriode(b.periode))}</strong> au titre du collaborateur ci-après désigné:</p>
    ${infoBox(sal)}
    <div class="nx-highlight-cards" style="grid-template-columns: repeat(2, 1fr);">
      <div class="nx-highlight-card"><div class="nx-hc-label">Cotisations employeur</div><div class="nx-hc-value">${fmtNumber(totalEmp)}</div><div class="nx-hc-sub">FCFA · taux ${formatPayrollRate(totalEmployerRate)}</div></div>
      <div class="nx-highlight-card cyan"><div class="nx-hc-label">Retenue salariale</div><div class="nx-hc-value">${fmtNumber(b.cnss_vid_sal)}</div><div class="nx-hc-sub">FCFA · taux ${formatPayrollRate(PAYROLL_PARAMETERS.cnssPensionEmployee)}</div></div>
    </div>
    <p style="margin-top:18px;"><strong>Ventilation par branche:</strong></p>
    <table class="nx-amounts">
      <thead><tr><th>Branche</th><th class="num">Taux</th><th class="num">Base plaf.</th><th class="num">Montant (FCFA)</th></tr></thead>
      <tbody>
        <tr><td>Prestations familiales et maternité — <em>employeur</em></td><td class="num">${formatPayrollRate(PAYROLL_PARAMETERS.cnssPfEmployer)}</td><td class="num">${fmtNumber(b.base_cnss)}</td><td class="num">${fmtNumber(ventilation.pf)}</td></tr>
        <tr><td>Accidents du travail et maladies professionnelles — <em>employeur</em></td><td class="num">${formatPayrollRate(PAYROLL_PARAMETERS.cnssRiskEmployer)}</td><td class="num">${fmtNumber(b.base_cnss)}</td><td class="num">${fmtNumber(ventilation.atmp)}</td></tr>
        <tr><td>Assurance vieillesse, invalidité et décès (VID) — <em>employeur</em></td><td class="num">${formatPayrollRate(PAYROLL_PARAMETERS.cnssPensionEmployer)}</td><td class="num">${fmtNumber(b.base_cnss)}</td><td class="num">${fmtNumber(ventilation.vid)}</td></tr>
        <tr><td>Assurance vieillesse, invalidité et décès (VID) — <em>salarié</em></td><td class="num">${formatPayrollRate(PAYROLL_PARAMETERS.cnssPensionEmployee)}</td><td class="num">${fmtNumber(b.base_cnss)}</td><td class="num">${fmtNumber(b.cnss_vid_sal)}</td></tr>
        <tr class="nx-total"><td><strong>TOTAL COTISATIONS CNSS</strong></td><td class="num"><strong>${formatPayrollRate(totalRate)}</strong></td><td class="num">—</td><td class="num"><strong>${fmtNumber(totalCnss)}</strong></td></tr>
      </tbody>
    </table>
    <div class="nx-legal-mention" style="background:#E8F5E9; border-left-color:#2E8B57;"><strong>Bordereau certifié:</strong> Établi conformément aux articles 39 et suivants du Code de Sécurité Sociale gabonais.</div>`;
}

export function buildAttestationHTML(
  type: AttestationType,
  sal: Salarie,
  t: Tenant | null,
  bulletin?: Bulletin,
  conge?: Conge,
  extras?: { conges?: readonly Conge[]; stc?: StcConfig },
): AttestationResult {
  const meta = TITRES[type];
  const now = new Date();
  const today = now.toLocaleDateString('fr-FR');
  const ville = t?.ville || 'Libreville';
  const refDoc = `ATT-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 9000) + 1000}`;

  let content = '';
  if (type === 'travail') content = contenuTravail(sal, t, ville);
  else if (type === 'salaire') content = contenuSalaire(sal, t, bulletin);
  else if (type === 'presence') content = contenuPresence(sal, t, today);
  else if (type === 'fin_contrat')
    content = contenuFinContrat(sal, t, today, extras?.conges ?? [], extras?.stc);
  else if (type === 'conges') content = contenuConges(sal, t, conge);
  else content = contenuCnss(sal, bulletin);

  const signature =
    type === 'fin_contrat'
      ? `<div class="nx-signature" style="justify-content: flex-start;"><div class="nx-date">Fait à <strong>${esc(ville)}</strong>, le <strong>${esc(today)}</strong></div></div>`
      : `<div class="nx-signature"><div class="nx-date">Fait à <strong>${esc(ville)}</strong>,<br>le <strong>${esc(today)}</strong></div><div class="nx-sign-block"><div class="nx-sign-line">La Direction</div><div class="nx-sign-role">Directeur des Ressources Humaines</div></div></div>`;

  const legal = companyLegalFromTenant(t);
  const companyName = legal.trade_name || legal.registered_name || 'Entreprise';
  const capitalLine =
    legal.capital > 0
      ? ` · Capital ${legal.capital.toLocaleString('fr-FR')} FCFA`
      : '';
  const formeLine = legal.legal_form ? `${legal.legal_form}${capitalLine}` : '';
  const theme = documentThemeFor(t);

  const html = `<div class="nx-attestation ${meta.cls}${brandRootClass(theme)}" id="nx-attestation-print"${brandRootStyleAttr(theme)}>
    ${brandFontStyleBlock(theme)}${brandCenterStripHtml(theme, companyName)}<div class="nx-banner">
      ${nxBannerLeftHtml(44, theme, companyName)}
      <div class="nx-banner-right">
        ${nxBannerRightLogoHtml(theme, companyName)}<div class="nx-co">${esc(companyName)}</div>
        <div class="nx-co-meta">${formeLine ? `${esc(formeLine)}<br>` : ''}${esc(legal.headquarters || '')}${legal.phone ? ` · ${esc(legal.phone)}` : ''}<br>NIF ${esc(legal.nif || '—')} · RCCM ${esc(legal.rccm || '—')}${brandHeaderTextHtml(theme)}</div>
      </div>
    </div>
    <div class="nx-doc-ref"><div>Réf. document <span class="nx-ref-num">${esc(refDoc)}</span></div><div>Édité le ${esc(today)}</div></div>
    <div class="nx-body">
      <h1 class="nx-title">${esc(meta.title)}</h1>
      ${meta.subtitle ? `<div class="nx-subtitle">${esc(meta.subtitle)}</div>` : ''}
      <div class="nx-content">${content}</div>
      ${signature}
    </div>
    <div class="nx-footer">
      <div><span class="nx-foot-co">${esc(t?.nom || '')}</span> &nbsp;·&nbsp; ${esc(t?.email || '')} &nbsp;·&nbsp; ${esc(t?.telephone || '')}</div>
      <div>Page 1/1 &nbsp;·&nbsp; Doc. ${esc(refDoc)}${theme.active && theme.footerText ? `<br>${esc(theme.footerText)}` : ''}</div>
    </div>
  </div>`;

  const filename = `${refDoc}_${sal.nom}_${sal.prenom}.pdf`.replace(/\s+/g, '_');
  return { title: meta.title, filename, html };
}
